import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Plus, Search, Pencil, Trash2, Flame, Beef, Wheat, Droplets, Leaf,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { API } from '@/lib/api';
import { DietasPlantilla } from '../components/nutrition/DietasPlantilla';

const SECCIONES = [
  { key: 'banco',  label: 'Banco de alimentos' },
  { key: 'dietas', label: 'Dietas pre-elaboradas' },
];

const CATEGORIAS = [
  { key: 'desayuno', label: 'Desayunos' },
  { key: 'comida',   label: 'Comidas'   },
  { key: 'cena',     label: 'Cenas'     },
  { key: 'snack',    label: 'Snacks'    },
];

const EMPTY_FORM = {
  nombre: '',
  categoria: 'desayuno',
  porcion: '',
  calorias: '',
  proteinas_g: '',
  carbohidratos_g: '',
  grasas_g: '',
  fibra_g: '',
  descripcion: '',
};

const MacroBadge = ({ icon: Icon, value, unit, colorClass, label }) => {
  if (value == null) return null;
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full', colorClass)}>
      <Icon className="h-3 w-3" />
      {value}{unit} <span className="opacity-70">{label}</span>
    </span>
  );
};

const AlimentoCard = ({ alimento, onEdit, onDelete }) => (
  <Card className="group hover:shadow-md transition-shadow">
    <CardContent className="p-4 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{alimento.nombre}</p>
          {alimento.porcion && (
            <p className="text-xs text-muted-foreground">{alimento.porcion}</p>
          )}
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(alimento)}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => onDelete(alimento)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {alimento.calorias != null && (
        <div className="flex items-center gap-1.5">
          <Flame className="h-4 w-4 text-orange-500" />
          <span className="text-lg font-bold text-orange-500">{alimento.calorias}</span>
          <span className="text-xs text-muted-foreground">kcal</span>
        </div>
      )}

      <div className="flex flex-wrap gap-1">
        <MacroBadge icon={Beef}   value={alimento.proteinas_g}      unit="g" label="P" colorClass="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" />
        <MacroBadge icon={Wheat}  value={alimento.carbohidratos_g}  unit="g" label="C" colorClass="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" />
        <MacroBadge icon={Droplets} value={alimento.grasas_g}       unit="g" label="G" colorClass="bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" />
        <MacroBadge icon={Leaf}   value={alimento.fibra_g}          unit="g" label="F" colorClass="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" />
      </div>

      {alimento.descripcion && (
        <p className="text-xs text-muted-foreground line-clamp-2">{alimento.descripcion}</p>
      )}
    </CardContent>
  </Card>
);

const SkeletonCard = () => (
  <Card>
    <CardContent className="p-4 flex flex-col gap-2">
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
      <Skeleton className="h-6 w-1/3" />
      <div className="flex gap-1">
        <Skeleton className="h-5 w-12 rounded-full" />
        <Skeleton className="h-5 w-12 rounded-full" />
      </div>
    </CardContent>
  </Card>
);

const NumericField = ({ label, name, value, onChange, placeholder }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-medium">{label}</label>
    <Input
      type="number"
      min="0"
      step="0.1"
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(name, e.target.value)}
    />
  </div>
);

export const AlimentosPage = () => {
  const [seccion, setSeccion] = useState('banco');
  const [categoriaActiva, setCategoriaActiva] = useState('desayuno');
  const [alimentos, setAlimentos]             = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [busqueda, setBusqueda]               = useState('');
  const [dialogOpen, setDialogOpen]           = useState(false);
  const [editando, setEditando]               = useState(null);
  const [form, setForm]                       = useState(EMPTY_FORM);
  const [saving, setSaving]                   = useState(false);
  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [deleting, setDeleting]               = useState(false);

  const fetchAlimentos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/alimentos`, {
        params: { categoria: categoriaActiva },
      });
      setAlimentos(res.data);
    } catch {
      toast.error('Error al cargar alimentos');
    } finally {
      setLoading(false);
    }
  }, [categoriaActiva]);

  useEffect(() => { fetchAlimentos(); }, [fetchAlimentos]);

  const alimentosFiltrados = alimentos.filter(a =>
    a.nombre.toLowerCase().includes(busqueda.toLowerCase())
  );

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ ...EMPTY_FORM, categoria: categoriaActiva });
    setDialogOpen(true);
  };

  const abrirEditar = (alimento) => {
    setEditando(alimento);
    setForm({
      nombre:          alimento.nombre,
      categoria:       alimento.categoria,
      porcion:         alimento.porcion         ?? '',
      calorias:        alimento.calorias         ?? '',
      proteinas_g:     alimento.proteinas_g      ?? '',
      carbohidratos_g: alimento.carbohidratos_g  ?? '',
      grasas_g:        alimento.grasas_g         ?? '',
      fibra_g:         alimento.fibra_g          ?? '',
      descripcion:     alimento.descripcion      ?? '',
    });
    setDialogOpen(true);
  };

  const handleFormChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const toPayload = () => ({
    nombre:          form.nombre.trim(),
    categoria:       form.categoria,
    porcion:         form.porcion.trim() || null,
    calorias:        form.calorias !== '' ? parseInt(form.calorias, 10) : null,
    proteinas_g:     form.proteinas_g !== '' ? parseFloat(form.proteinas_g) : null,
    carbohidratos_g: form.carbohidratos_g !== '' ? parseFloat(form.carbohidratos_g) : null,
    grasas_g:        form.grasas_g !== '' ? parseFloat(form.grasas_g) : null,
    fibra_g:         form.fibra_g !== '' ? parseFloat(form.fibra_g) : null,
    descripcion:     form.descripcion.trim() || null,
  });

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    setSaving(true);
    try {
      if (editando) {
        await axios.put(`${API}/alimentos/${editando.id}`, toPayload());
        toast.success('Alimento actualizado');
      } else {
        await axios.post(`${API}/alimentos`, toPayload());
        toast.success('Alimento creado');
      }
      setDialogOpen(false);
      if (form.categoria === categoriaActiva) {
        fetchAlimentos();
      } else {
        setCategoriaActiva(form.categoria);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/alimentos/${deleteTarget.id}`);
      toast.success('Alimento eliminado');
      setDeleteTarget(null);
      fetchAlimentos();
    } catch {
      toast.error('Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Alimentos y Dietas</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {seccion === 'banco'
              ? 'Crea tu biblioteca de alimentos para armar planes más rápido'
              : 'Dietas pre-elaboradas listas para asignar y ajustar por paciente'}
          </p>
        </div>
        {seccion === 'banco' && (
          <Button onClick={abrirNuevo} className="gap-2 self-start sm:self-auto">
            <Plus className="h-4 w-4" />
            Nuevo Alimento
          </Button>
        )}
      </div>

      {/* Tabs de sección: banco | dietas */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit mb-6">
        {SECCIONES.map(s => (
          <button
            key={s.key}
            onClick={() => setSeccion(s.key)}
            className={cn(
              'px-4 py-1.5 rounded-md text-sm font-medium transition-all cursor-pointer',
              seccion === s.key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {seccion === 'dietas' ? (
        <DietasPlantilla />
      ) : (
      <>
      {/* Tabs de categoría */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit mb-6">
        {CATEGORIAS.map(cat => (
          <button
            key={cat.key}
            onClick={() => { setCategoriaActiva(cat.key); setBusqueda(''); }}
            className={cn(
              'px-4 py-1.5 rounded-md text-sm font-medium transition-all cursor-pointer',
              categoriaActiva === cat.key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Búsqueda */}
      <div className="relative mb-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar alimento..."
          className="pl-9"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
      </div>

      {/* Contador */}
      {!loading && (
        <p className="text-sm text-muted-foreground mb-4">
          {alimentosFiltrados.length} alimento{alimentosFiltrados.length !== 1 ? 's' : ''}
          {busqueda && ` para "${busqueda}"`}
        </p>
      )}

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : alimentosFiltrados.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
            <Flame className="h-8 w-8 text-muted-foreground" />
          </div>
          <p className="font-semibold text-lg">
            {busqueda ? 'Sin resultados' : `No hay ${CATEGORIAS.find(c => c.key === categoriaActiva)?.label.toLowerCase()} aún`}
          </p>
          <p className="text-muted-foreground text-sm mt-1 max-w-xs">
            {busqueda
              ? 'Prueba con otro término de búsqueda'
              : 'Agrega tu primer alimento con el botón de arriba'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {alimentosFiltrados.map(a => (
            <AlimentoCard
              key={a.id}
              alimento={a}
              onEdit={abrirEditar}
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      )}
      </>
      )}

      {/* Modal crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar Alimento' : 'Nuevo Alimento'}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-2">
            {/* Nombre */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Nombre <span className="text-destructive">*</span></label>
              <Input
                placeholder="Ej. Avena con fruta"
                value={form.nombre}
                onChange={e => handleFormChange('nombre', e.target.value)}
              />
            </div>

            {/* Categoría + Porción */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Categoría</label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={form.categoria}
                  onChange={e => handleFormChange('categoria', e.target.value)}
                >
                  {CATEGORIAS.map(c => (
                    <option key={c.key} value={c.key}>{c.label.slice(0, -1)}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Porción</label>
                <Input
                  placeholder="Ej. 1 taza (240ml)"
                  value={form.porcion}
                  onChange={e => handleFormChange('porcion', e.target.value)}
                />
              </div>
            </div>

            {/* Calorías */}
            <NumericField
              label="Calorías (kcal)"
              name="calorias"
              value={form.calorias}
              onChange={handleFormChange}
              placeholder="Ej. 350"
            />

            {/* Macros */}
            <div>
              <p className="text-sm font-medium mb-2">Macronutrientes (g por porción)</p>
              <div className="grid grid-cols-2 gap-3">
                <NumericField label="Proteínas"       name="proteinas_g"     value={form.proteinas_g}     onChange={handleFormChange} placeholder="0" />
                <NumericField label="Carbohidratos"   name="carbohidratos_g" value={form.carbohidratos_g} onChange={handleFormChange} placeholder="0" />
                <NumericField label="Grasas"          name="grasas_g"        value={form.grasas_g}        onChange={handleFormChange} placeholder="0" />
                <NumericField label="Fibra"           name="fibra_g"         value={form.fibra_g}         onChange={handleFormChange} placeholder="0" />
              </div>
            </div>

            {/* Descripción */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Descripción / notas</label>
              <textarea
                className="flex min-h-[72px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                placeholder="Ingredientes, preparación, notas..."
                value={form.descripcion}
                onChange={e => handleFormChange('descripcion', e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : editando ? 'Guardar cambios' : 'Crear alimento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal confirmar eliminar */}
      <Dialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar alimento</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            ¿Eliminar <strong>{deleteTarget?.nombre}</strong>? Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
